import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/Seo";
import AcademyExam from "@/components/AcademyExam";
import { academyApi } from "@/lib/academy";

export default function AcademyBelt() {
  const { belt } = useParams();
  const [data, setData] = useState(null);
  const [needsAuth, setNeedsAuth] = useState(false);
  const [showExam, setShowExam] = useState(false);
  const [labText, setLabText] = useState("");
  const [labMsg, setLabMsg] = useState("");
  const [earned, setEarned] = useState(false);

  useEffect(() => {
    academyApi.overview().then((ov) => {
      const b = ov.belts.find((x) => x.id === belt);
      if (!b) return;
      setData({ belt: b, lessons: ov.lessons.filter((l) => l.belt === b.name), labs: ov.labs.filter((l) => l.belt === b.name) });
    }).catch(() => {});
    academyApi.dashboard().then((d) => {
      if (d.passedBelts?.includes((data || {}).belt?.name)) setEarned(true);
      setNeedsAuth(false);
    }).catch((e) => { if (e.response?.status === 401) setNeedsAuth(true); });
  }, [belt]);

  if (!data) return <section className="wrap" style={{ padding: 80 }}><p>Loading belt…</p></section>;
  const { belt: b, lessons, labs } = data;

  return (
    <>
      <Seo title={`${b.name} Belt | Solar Black Belt Academy`} description={b.summary} />
      <section className="page-hero"><div className="wrap">
        <span className="eyebrow">{b.name.toUpperCase()} BELT · {b.order}/8</span>
        <h1>{b.name} Belt</h1>
        <p className="lead">{b.summary}</p>
        {needsAuth && (
          <p className="micro" data-testid="academy-belt-signin-hint" style={{ marginTop: 10 }}>
            <Link className="btn small lime" to="/academy/login" state={{ from: `/academy/belts/${b.id}` }}>Sign in</Link>
            {"  "}to start exams, submit labs and track your progress — or <Link className="textbutton" to="/academy/register">enroll free</Link>.
          </p>
        )}
      </div></section>
      <section className="wrap academy-section">
        <div className="academy-cert-grid">
          {lessons.map((l) => (
            <Link to={`/academy/lessons/${l.id}`} key={l.id} className="academy-belt-card" data-testid={`academy-lesson-${l.id}`}>
              <img src={b.scene} alt={`${b.name} Belt scene`} loading="lazy" />
              <h3>{l.title}</h3>
              <span className="micro">≈ {l.estimatedMinutes} min · {l.status === "approved" ? "Published" : "Awaiting technical review"}</span>
            </Link>
          ))}
        </div>
      </section>
      {labs.map((lab) => (
        <section className="wrap academy-section" key={lab.id} data-testid={`academy-lab-${lab.id}`}>
          <span className="eyebrow">PRACTICAL LAB</span>
          <h2>{lab.title}</h2>
          <p className="lead" style={{ maxWidth: 760 }}>{lab.brief}</p>
          <textarea rows={4} maxLength={8000} value={labText} onChange={(e) => setLabText(e.target.value)}
            placeholder="Describe your work, your math and your sources…" style={{ width: "100%", maxWidth: 760 }} data-testid={`academy-lab-input-${lab.id}`} />
          <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
            <button className="btn small lime" onClick={async () => { try { await academyApi.submitLab(lab.id, labText); setLabMsg("Submitted for reviewer grading."); } catch (e) { setLabMsg(e.response?.data?.detail || "Failed."); } }}>Submit for review</button>
            {labMsg && <span className="micro">{labMsg}</span>}
          </div>
        </section>
      ))}
      <section className="wrap academy-section" data-testid="academy-belt-exam">
        <span className="eyebrow">BELT EXAM</span>
        <h2>Ready to grade for your {b.name} Belt?</h2>
        <p className="micro">Threshold {b.pass_percent}% · every safety-critical item must be correct · {b.review_required ? "includes reviewer sign-off" : "self-paced"} · three attempts per day.</p>
        {earned
          ? <p className="lead" style={{ color: "var(--teal)" }}>✓ {b.name} Belt earned. Your certificate is on your dashboard.</p>
          : showExam
            ? <AcademyExam scope={{ kind: "belt", id: b.id }} onPassed={() => setEarned(true)} onClose={() => setShowExam(false)} />
            : <button className="btn lime" data-testid="academy-belt-exam-open" onClick={() => setShowExam(true)}>Start the {b.name} Belt exam</button>}
        <p className="micro" style={{ marginTop: 14 }}>Belt certificates are issued by an administrator after the server verifies every requirement — lessons, exam, written task, lab{b.review_required ? " and reviewer approval" : ""}.</p>
      </section>
    </>
  );
}
